// src/context/ThemeContext.jsx
// Theme context for sharing theme state across the app

import React, { createContext, useContext, useState, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { lightTheme, darkTheme } from "../theme";

const ThemeContext = createContext(null);

const THEME_STORAGE_KEY = "blayna_theme";

export function ThemeProvider({ children }) {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  const [mode, setMode] = useState(stored || "light"); // Default to light

  const activeTheme = useMemo(
    () => (mode === "light" ? lightTheme : darkTheme),
    [mode]
  );

  const toggleTheme = () => {
    const next = mode === "light" ? "dark" : "light";
    setMode(next);
    localStorage.setItem(THEME_STORAGE_KEY, next);
  };

  const value = {
    mode,
    toggleTheme,
    isDark: mode === "dark",
    isLight: mode === "light",
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={activeTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }
  return context;
}
